import { useEffect, useState, useCallback } from 'react'
import { api } from '../api/client'
import { ConfirmDialog, Toast } from '../components/ConfirmDialog'
import { thisMonthJst } from '../utils/jst'

function fmt(n: number | null | undefined) { return (n ?? 0).toLocaleString('ja-JP') }
const MONTH_STORAGE_KEY = 'mangaos_month'
 
function getSavedMonth(): string {
  try {
    const saved = localStorage.getItem(MONTH_STORAGE_KEY)
    if (saved && /^\d{4}-\d{2}$/.test(saved)) return saved
  } catch {}
  return thisMonthJst()
}

// ────────────────────────────────────────
// 完了済み話数の判定
// ────────────────────────────────────────
const DONE_STATUSES = new Set(['completed', 'submitted', 'hiatus'])

function isDoneEpisode(ep: any): boolean {
  const status = String(ep?.status ?? ep?.episode_status ?? '').toLowerCase()
  if (DONE_STATUSES.has(status)) return true

  const stagesDone = Number(ep?.stages_done ?? ep?.done_stages ?? NaN)
  const stagesTotal = Number(ep?.stages_total ?? ep?.total_stages ?? 7)
  if (Number.isFinite(stagesDone) && stagesDone >= stagesTotal && stagesTotal > 0) return true

  const summary = String(ep?.stage_summary ?? '')
  if (summary) {
    const pairs = summary.split(',').map((pair) => pair.split(':'))
    const known = pairs.filter(([stage, st]) => stage && st)
    if (known.length > 0 && known.every(([, st]) => st === 'done')) return true
  }
  return false
}

function isUrgentEpisode(ep: any): boolean {
  if (isDoneEpisode(ep)) return false
  const days = ep?.days_until
  return days !== null && days !== undefined && Number(days) <= 2
}

function daysLabel(days: number | null) {
  if (days === null) return '—'
  if (days < 0)  return `${Math.abs(days)}日超過`
  if (days === 0) return '今日'
  return `残${days}日`
}

function daysChipStyle(days: number | null) {
  if (days === null) return {}
  if (days < 0 || days <= 2) return {
    background: 'var(--red-light)', color: 'var(--red)',
    borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600,
  }
  if (days <= 7) return {
    background: 'var(--amber-light)', color: 'var(--amber)',
    borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600,
  }
  return {
    background: 'var(--paper-2)', color: 'var(--ink-3)',
    borderRadius: 4, padding: '2px 8px', fontSize: 11,
  }
}

// ────────────────────────────────────────
// 指標カード
// ────────────────────────────────────────
function KpiCard({ label, value, sub, color, prefix = '¥' }: {
  label: string; value: number | string; sub?: string
  color?: 'green' | 'red' | 'amber'; prefix?: string
}) {
  const colorMap = { green: 'var(--green)', red: 'var(--red)', amber: 'var(--amber)' }
  return (
    <div className="metric">
      <div className="metric-label">{label}</div>
      <div className="metric-value" style={{ color: color ? colorMap[color] : undefined, fontSize: 18 }}>
        {typeof value === 'number'
          ? (value < 0 ? `-${prefix}${fmt(Math.abs(value))}` : `${prefix}${fmt(value)}`)
          : value}
      </div>
      {sub && <div className="metric-sub">{sub}</div>}
    </div>
  )
}

// ────────────────────────────────────────
// セクションヘッダー
// ────────────────────────────────────────
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        fontSize: 11, fontFamily: 'var(--font-mono)', letterSpacing: '0.1em',
        textTransform: 'uppercase', color: 'var(--ink-3)',
        borderBottom: '1px solid var(--paper-3)', paddingBottom: 6, marginBottom: 14,
      }}>
        {title}
      </div>
      {children}
    </div>
  )
}

// ────────────────────────────────────────
// メインページ
// ────────────────────────────────────────
export default function MonthlyReport() {
const [month, setMonthState] = useState<string>(getSavedMonth)
const setMonth = (m: string) => {
  setMonthState(m)
  try { localStorage.setItem(MONTH_STORAGE_KEY, m) } catch {}
}
  const [data, setData]       = useState<any>(null)
  const [closeData, setClose] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr]         = useState<string | null>(null)
  const [closing, setClosing]   = useState(false)
  const [reopenNote, setReopenNote] = useState('')
  const [showReopen, setShowReopen] = useState(false)
  const [toast, setToast]       = useState<{ ok: boolean; msg: string } | null>(null)
  const [showApproveDialog, setShowApproveDialog] = useState(false)

  const showToast = (ok: boolean, msg: string) => {
    setToast({ ok, msg })
    setTimeout(() => setToast(null), 5000)
  }

  const load = useCallback(async () => {
    setLoading(true)
    setErr(null)
    try {
      const [result, close] = await Promise.all([
        api.monthlyReport.get(month),
        api.monthlyCloses.get(month).catch(() => ({ status: 'open' })),
      ])
      setData(result)
      setClose(close)
    } catch (e: any) {
      setErr(e.message)
    } finally {
      setLoading(false)
    }
  }, [month])

  useEffect(() => { load() }, [load])

  const closeStatus: string = closeData?.status ?? 'open'
  const isClosed = closeStatus === 'closed'

  const startReview = async () => {
    setClosing(true)
    try { await api.monthlyCloses.review(month); await load(); showToast(true, 'レビューを開始しました') }
    catch (e: any) { showToast(false, e.message) }
    finally { setClosing(false) }
  }

  const approve = async () => {
    setClosing(true)
    try { await api.monthlyCloses.approve(month); await load(); showToast(true, `${month} を締めました`) }
    catch (e: any) { showToast(false, e.message) }
    finally { setClosing(false) }
  }

  const reopen = async () => {
    if (!reopenNote.trim()) { showToast(false, '解除理由を入力してください'); return }
    setClosing(true)
    try {
      await api.monthlyCloses.reopen(month, reopenNote)
      setShowReopen(false)
      setReopenNote('')
      await load()
      showToast(true, '締めを解除しました')
    } catch (e: any) { showToast(false, e.message) }
    finally { setClosing(false) }
  }

  if (err) return <div className="error-box">{err}</div>

  const rev  = data?.revenue
  const exp  = data?.expenses
  const lab  = data?.labor
  const helpLab = data?.help_labor  // ヘルプスタッフ人件費の内訳（labor合計・支出合計・税引前利益には既に合算済み）
  const pay  = data?.payments
  const sum  = data?.summary
  // shift_status.total は API 側で「現在シフト提出対象のアシスタント数」
  // （退職済み・シフト対象外・payroll_enabled=0 を除外済み）として返る。
  // worker/src/lib/shift_eligibility.ts 参照。
  const shft = data?.shift_status ?? null

  const activeEpisodes = (data?.episodes ?? []).filter((ep: any) => !isDoneEpisode(ep))
  const hasUrgentActiveEpisode = activeEpisodes.some(isUrgentEpisode)
  const shiftTotal = Number(shft?.total ?? 0)
  const shiftSubmitted = Number(shft?.submitted ?? 0)
  const shiftConfirmed = Number(shft?.confirmed ?? 0)
  const shiftAllSubmitted = shiftTotal === 0 || shiftSubmitted >= shiftTotal
  const visibleAlerts = (data?.alerts ?? []).filter((a: any) => {
    const msg = String(a?.message ?? '')
    const isDeadlineEpisodeAlert = msg.includes('締切') && msg.includes('話数')
    const isShiftMissingAlert = msg.includes('シフト') && msg.includes('未提出')
    if (isDeadlineEpisodeAlert && !hasUrgentActiveEpisode) return false
    if (isShiftMissingAlert && shiftAllSubmitted) return false
    return true
  })

  const laborTarget = 450000
  // 内部人件費 = 振込ベース + 食費（会社直接負担）+ ヘルプスタッフ費用
  // total は daily_total 合計（振込ベース）、meal_total は meal_provided × meal_rate
  const mealTotal      = lab?.meal_total ?? 0
  const helpLaborTotal = helpLab?.total ?? 0
  const laborCost      = (lab?.total ?? 0) + mealTotal + helpLaborTotal  // 内部管理上の人件費（食費込み・ヘルプ込み）
  const laborOver      = laborCost > laborTarget

  return (
    <div>
      {toast && <Toast msg={toast.msg} ok={toast.ok} onClose={() => setToast(null)} />}

      {/* 承認確認ダイアログ */}
      {showApproveDialog && (
        <ConfirmDialog
          title="承認・締め処理"
          message={`${month} を承認・締めします。\n\n締め後にできること：\n✓ アシスタントへの支払完了記録\n✓ CSV出力（approved のみ対象）\n\n締め後にできないこと：\n✕ 固定費・支出の支払状態変更\n✕ 勤怠の追加・変更\n\n必要な場合は締めを解除できます。`}
          confirmLabel="承認・締める"
          onConfirm={async () => { setShowApproveDialog(false); await approve() }}
          onClose={() => setShowApproveDialog(false)}
        />
      )}

      {/* コントロール */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: 'auto' }} />
          <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}>
            {loading ? '集計中...' : '更新'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {closeStatus === 'closed' && (
            <span style={{
              background: 'var(--blue-light)', color: 'var(--blue)',
              borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600,
            }}>🔒 締め済み</span>
          )}
          {closeStatus === 'reviewing' && (
            <span style={{
              background: 'var(--amber-light)', color: 'var(--amber)',
              borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600,
            }}>📋 レビュー中</span>
          )}
          {data && !isClosed && closeStatus === 'open' && (
            <button className="btn btn-outline btn-sm" onClick={startReview} disabled={closing}>
              {closing ? '…' : 'レビュー開始'}
            </button>
          )}
          {data && !isClosed && closeStatus === 'reviewing' && (
            <button className="btn btn-primary btn-sm"
              onClick={() => setShowApproveDialog(true)} disabled={closing}>
              ✓ 承認・締め処理
            </button>
          )}
          {data && isClosed && (
            <button className="btn btn-ghost btn-sm" onClick={() => setShowReopen(!showReopen)}
              style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              締めを解除
            </button>
          )}
          {data && (
            <a href={api.monthlyReport.csvUrl(month)} className="btn btn-outline btn-sm" download>
              レポートCSV
            </a>
          )}
        </div>
      </div>

      {/* 締め解除フォーム */}
      {showReopen && (
        <div style={{
          background: 'var(--paper-2)', border: '1px solid var(--paper-3)',
          borderRadius: 8, padding: '14px 18px', marginBottom: 16,
          display: 'flex', gap: 10, alignItems: 'center',
        }}>
          <span style={{ fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap' }}>解除理由（必須）</span>
          <input type="text" placeholder="例: 勤怠入力漏れのため" value={reopenNote}
            onChange={(e) => setReopenNote(e.target.value)}
            style={{ flex: 1, fontSize: 13 }} />
          <button className="btn btn-danger btn-sm" onClick={reopen} disabled={closing}>
            {closing ? '…' : '解除する'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowReopen(false)}>キャンセル</button>
        </div>
      )}

      {loading ? (
        <div className="loading">LOADING...</div>
      ) : data && (
        <>
          {/* ── アラートバナー ── */}
          {visibleAlerts.length > 0 && (
            <div style={{ marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {visibleAlerts.map((a: any, i: number) => (
                <div key={i} style={{
                  padding: '10px 16px', borderRadius: 6, fontSize: 13, fontWeight: 500,
                  background: a.level === 'critical' ? 'var(--red-light)' : a.level === 'warning' ? 'var(--amber-light)' : 'var(--blue-light)',
                  color:      a.level === 'critical' ? 'var(--red)' : a.level === 'warning' ? 'var(--amber)' : 'var(--blue)',
                  border: `1px solid ${a.level === 'critical' ? 'var(--red-border)' : a.level === 'warning' ? 'rgba(176,122,18,0.2)' : 'var(--blue-border)'}`,
                }}>
                  {a.level === 'critical' ? '⚠ ' : a.level === 'warning' ? '！ ' : 'ℹ '}{a.message}
                </div>
              ))}
            </div>
          )}

          {/* ── 収支サマリー ── */}
          <Section title="収支サマリー">
            <div className="grid-4" style={{ marginBottom: 12 }}>
              <KpiCard label="入金済み" value={rev?.received ?? 0} color={rev?.received > 0 ? 'green' : undefined} />
              <KpiCard label="税積立（先取）" value={rev?.tax_reserved ?? 0} color="red" />
              <KpiCard label="支出合計" value={sum?.total_expenses ?? 0} />
              <KpiCard
                label="税引前利益"
                value={sum?.operating_profit ?? 0}
                color={(sum?.operating_profit ?? 0) >= 0 ? 'green' : 'red'}
              />
            </div>
            <div className="grid-4">
              <KpiCard label="今使えるお金" value={sum?.spendable ?? 0}
                color={(sum?.spendable ?? 0) >= 0 ? 'green' : 'red'}
                sub="入金 − 税積立 − 未払固定費" />
              <KpiCard label="未入金（今月分）" value={rev?.pending_this_month ?? 0}
                prefix="¥" sub={`全期間: ¥${fmt(rev?.pending_all ?? 0)}`} />
              <KpiCard label="未払い支出" value={exp?.unpaid ?? 0}
                color={(exp?.unpaid_count ?? 0) > 0 ? 'red' : undefined}
                sub={`${exp?.unpaid_count ?? 0}件 / 期限超過${exp?.overdue_count ?? 0}件`} />
              <KpiCard label="人件費比率" value={sum?.labor_ratio !== null ? `${sum?.labor_ratio}%` : '—'}
                prefix=""
                color={(sum?.labor_ratio ?? 0) > 30 ? 'amber' : undefined}
                sub="入金に対する人件費割合" />
            </div>
          </Section>

          {/* ── 人件費 ── */}
          <Section title="人件費">
            <div className="grid-4">
              <KpiCard label="人件費合計（食費・ヘルプ込）" value={laborCost}
                color={laborOver ? 'red' : undefined}
                sub={`目標上限 ¥${fmt(laborTarget)}${helpLaborTotal > 0 ? `（うちヘルプ ¥${fmt(helpLaborTotal)}）` : ''}`} />
              <KpiCard label="人足数（延べ）" value={lab?.man_days ?? 0} prefix=""
                sub="延べ出勤回数（目標 27人足）" />
              <KpiCard label="出勤日数" value={lab?.working_days ?? 0} prefix="" sub="誰かが出勤したユニーク日数" />
              <KpiCard label="稼働人数" value={lab?.assistants_worked ?? 0} prefix="" sub="1回以上出勤したアシスタント数" />
            </div>
            {/* 人件費バー */}
            <div style={{ marginTop: 12 }}>
              <div style={{
                height: 10, background: 'var(--paper-3)', borderRadius: 5, overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%',
                  width: `${Math.min(100, Math.round(laborCost / laborTarget * 100))}%`,
                  background: laborOver ? 'var(--red)' : 'var(--green)',
                  borderRadius: 5, transition: 'width 0.4s',
                }} />
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>
                ¥{fmt(laborCost)} / ¥{fmt(laborTarget)}（{Math.round(laborCost / laborTarget * 100)}%）
              </div>
            </div>
          </Section>

          {/* ── ヘルプスタッフ人件費（内訳カード。金額は上記「人件費合計」・支出合計・税引前利益に合算済み） ── */}
          <Section title="ヘルプスタッフ人件費（内訳）">
            <div className="grid-4">
              <KpiCard label="合計（無効化を除く）" value={helpLab?.total ?? 0} />
              <KpiCard label="未提出" value={helpLab?.pending_total ?? 0} />
              <KpiCard label="提出済み（未確認）" value={helpLab?.submitted_total ?? 0} />
              <KpiCard label="確認済み（未払い）" value={helpLab?.confirmed_total ?? 0} />
            </div>
            <div className="grid-4" style={{ marginTop: 12 }}>
              <KpiCard label="支払済み" value={helpLab?.paid_total ?? 0} color="green" />
              <KpiCard label="勤務件数" value={helpLab?.entries_count ?? 0} prefix="" />
              <KpiCard label="稼働人数" value={helpLab?.staff_count ?? 0} prefix="" />
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8 }}>
              内訳の表示です（金額は上記「人件費合計」・支出合計・税引前利益にすでに含まれています）。管理・支払確定は「ヘルプスタッフ管理」画面から行います。
            </div>
          </Section>

          {/* ── 月次支払 ── */}
          <Section title="月次支払">
            <div className="grid-4">
              <KpiCard label="支払合計" value={pay?.grand_total ?? 0} />
              <KpiCard label="支払済" value={`${pay?.paid_count ?? 0}/${pay?.total ?? 0}名`} prefix=""
                color={(pay?.paid_count ?? 0) === (pay?.total ?? 0) && (pay?.total ?? 0) > 0 ? 'green' : undefined} />
              <KpiCard label="確認済（未払）" value={`${pay?.confirmed_count ?? 0}名`} prefix="" />
              <KpiCard label="未確認" value={`${pay?.draft_count ?? 0}名`} prefix=""
                color={(pay?.draft_count ?? 0) > 0 ? 'amber' : undefined} />
            </div>
          </Section>

          <div className="grid-2" style={{ gap: 16, marginBottom: 24 }}>
            {/* ── 制作・締切 ── */}
            <div>
              <Section title="制作・締切">
                <div className="card">
                  {activeEpisodes.length === 0 ? (
                    <div style={{ padding: '24px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
                      対象話数なし
                    </div>
                  ) : (
                    <div>
                      {activeEpisodes.map((ep: any) => (
                        <div key={ep.id} style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '10px 16px', borderBottom: '1px solid var(--paper-2)',
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>
                              {ep.work_title}
                              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-3)', marginLeft: 6 }}>
                                #{ep.episode_number}
                              </span>
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                              {ep.estimated_fee ? `¥${fmt(ep.estimated_fee)}` : '—'} ／
                              工程 {ep.stages_done}/7
                            </div>
                          </div>
                          <span style={daysChipStyle(ep.days_until)}>
                            {daysLabel(ep.days_until)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Section>
            </div>

            {/* ── シフト・人員 / 作品別収益 ── */}
            <div>
              <Section title="シフト状況">
                <div className="card" style={{ marginBottom: 16 }}>
                  <div style={{ padding: '14px 18px' }}>
                    <div className="grid-2" style={{ gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>希望提出</div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 600 }}>
                          {shiftSubmitted}/{shiftTotal}名
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>シフト確定</div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 600,
                          color: shiftConfirmed === shiftTotal ? 'var(--green)' : 'var(--amber)' }}>
                          {shiftConfirmed}/{shiftTotal}名
                        </div>
                      </div>
                    </div>
                    {/* 提出率バー */}
                    {shiftTotal > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ height: 6, background: 'var(--paper-3)', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%',
                            width: `${Math.round(shiftSubmitted / shiftTotal * 100)}%`,
                            background: shiftSubmitted === shiftTotal ? 'var(--green)' : 'var(--amber)',
                            borderRadius: 3,
                          }} />
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 3 }}>
                          提出率 {Math.round(shiftSubmitted / shiftTotal * 100)}%
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Section>

              <Section title="作品別収益">
                <div className="card">
                  {data.work_breakdown.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>データなし</div>
                  ) : data.work_breakdown.map((w: any) => (
                    <div key={w.title} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 16px', borderBottom: '1px solid var(--paper-2)',
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500, fontSize: 13 }}>{w.title}</div>
                        <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>{w.frequency}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        {w.received > 0 && (
                          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>
                            ¥{fmt(w.received)}
                          </div>
                        )}
                        {w.pending > 0 && (
                          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-3)' }}>
                            未入金 ¥{fmt(w.pending)}
                          </div>
                        )}
                        {w.received === 0 && w.pending === 0 && (
                          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>収入なし</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            </div>
          </div>

          {/* フッター */}
          <div style={{ fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)', textAlign: 'right' }}>
            生成: {data.generated_at?.slice(0, 19).replace('T', ' ')}
          </div>
        </>
      )}
    </div>
  )
}